import React, { useRef, forwardRef, type ReactElement } from "react";
import { AdornmentWrapper, UnderlinedText } from "./components";
import { useForkRef } from "../useForkRef";
import { clsxm } from "../clsxm";
import { CheckCircle } from "../icons/CheckCircle";
import { EditIcon } from "../icons/EditIcon";
import { LockIcon } from "../icons/LockIcon";
import { WarningCircle } from "../icons/WarningCircle";

export const BASE_INPUT_CLS =
  "bg-transparent focus-visible:outline-0 focus:outline-0 placeholder:text-text-base placeholder:Body-M-Medium placeholder:2xl:Body-L-Medium placeholder:4xl:Body-4XL-Medium w-full text-text-base";
export const INPUT_ICON_CLS = "s-[24px] 4xl:s-[36px]";
export const INPUT_HEIGHT_CLS = "h-[48px] 2xl:h-[56px] 4xl:h-[80px]";
export const INPUT_FONT_CLS =
  "Body-M-Medium 2xl:Body-L-Medium 4xl:Body-4XL-Medium";
export const INPUT_LABEL_CLS =
  "Body-S-Medium 2xl:Body-M-Medium 4xl:Body-XL-Medium text-grayscale-4";
export const INPUT_PLACEHOLDER_CLS =
  "Body-M-Medium 2xl:Body-XL-Medium 4xl:Body-2XL-Medium";

export interface PInput extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  errorMessage?: string;
  successMessage?: string;
  isError?: boolean;
  isSuccess?: boolean;
  helperText?: string;
  additionalContent?: React.ReactNode;
  isEditable?: boolean;
  isUnderlinedTextVisible?: boolean;
  wrapperClassName?: string;
  inputClassName?: string;
  labelClassName?: string;
  onEditClick?: VoidFunction;
  readOnly?: boolean;
  isIconIndicatorVisible?: boolean;
  topRightAboveContent?: ReactElement | string;
}

export const BaseInput = forwardRef<HTMLInputElement, PInput>(
  function BaseInput(
    {
      className,
      label,
      successMessage,
      startAdornment,
      endAdornment,
      isError,
      isSuccess,
      helperText,
      errorMessage = undefined,
      additionalContent,
      isEditable = true,
      isUnderlinedTextVisible = true,
      onClick,
      wrapperClassName,
      inputClassName,
      labelClassName,
      onEditClick,
      readOnly = false,
      isIconIndicatorVisible = true,
      topRightAboveContent,
      ...props
    },
    ref
  ) {
    const nodeRef = useRef<HTMLInputElement>(null);
    const handleRef = useForkRef<HTMLInputElement>(nodeRef, ref);
    const underlinedText = errorMessage || successMessage || helperText;

    return (
      <div
        className={clsxm("flex flex-col w-full align-middle", wrapperClassName)}
        onClick={onClick}
      >
        {label && (
          <div className="flex flex-row gap-2 items-baseline mb-[6px] relative">
            <p className={clsxm(INPUT_LABEL_CLS, labelClassName)}>{label}</p>
            {!!additionalContent && additionalContent}
            {!!topRightAboveContent && (
              <div className="absolute top-o end-0 text-grayscale-4">
                {topRightAboveContent}
              </div>
            )}
          </div>
        )}
        <div
          className={clsxm(
            "flex flex-row w-full align-middle justify-center gap-[10px] border-[1px] rounded-[8px] border-border-base text-border-base fill-border-base cursor-text px-[12px] 4xl:px-[18px] items-center",
            INPUT_HEIGHT_CLS,
            inputClassName,
            !readOnly && "focus-within:border-border-tab-mobile",
            isError && "border-status-red focus-within:border-status-red",
            !!isSuccess && "border-primary-3 focus-within:border-primary-3"
          )}
        >
          {startAdornment && (
            <AdornmentWrapper>{startAdornment}</AdornmentWrapper>
          )}
          <input
            ref={handleRef}
            {...props}
            className={clsxm(
              BASE_INPUT_CLS,
              "h-[24px] 4xl:h-[40px]",
              INPUT_FONT_CLS,
              className
            )}
            type={props.type || "text"}
            readOnly={readOnly}
            autoComplete="off"
            style={{ outline: "none" }}
          />
          {!label && additionalContent}
          {endAdornment && <AdornmentWrapper>{endAdornment}</AdornmentWrapper>}
          {isError && isIconIndicatorVisible && (
            <AdornmentWrapper className="text-status-red">
              <WarningCircle className={INPUT_ICON_CLS} />
            </AdornmentWrapper>
          )}
          {isSuccess && isIconIndicatorVisible && (
            <AdornmentWrapper className="text-primary-3">
              <CheckCircle className={INPUT_ICON_CLS} />
            </AdornmentWrapper>
          )}
          {isEditable && readOnly && !props.disabled && (
            <AdornmentWrapper
              role="button"
              onClick={() => {
                onEditClick && onEditClick();
              }}
            >
              <EditIcon className={clsxm("text-grayscale-3", INPUT_ICON_CLS)} />
            </AdornmentWrapper>
          )}
          {props.disabled && (
            <AdornmentWrapper>
              <LockIcon className="text-grayscale-3" />
            </AdornmentWrapper>
          )}
        </div>
        {underlinedText && isUnderlinedTextVisible && (
          <div className="mt-[5px]">
            <UnderlinedText
              isError={isError}
              isSuccess={!!successMessage}
              underlinedText={underlinedText}
            />
          </div>
        )}
      </div>
    );
  }
);
